import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useLDClient } from '@afterpaytouch/feature'
import { useEffect, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useIsEmptyCheckoutToken,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  // Mirrors the routing in LoggedIn.tsx — only users who would actually be sent down the
  // experimental no-payment-method branch should be enrolled, otherwise variant A and control
  // behave identically and dilute the analysis population. `selectPreferredCard` collapses the
  // full saved-card / fallback state into preferredCard.card: it's null only when the user has
  // no preferred and no usable fallback, and non-null in every other case (including the
  // errored-preferred-with-no-fallback edge), so this check matches LoggedIn's
  // `preferredCard?.card !== null` routing guard exactly.
  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

// Returns a callback that fires the Triggered Experiment event for variant-A users. Caller is
// responsible for invoking this only when the user is actually routed to the experimental
// no-payment-method branch (currently from LoggedIn.tsx, gated on requestCompleted +
// hasFeatureFlagsReady). The eligibility hook above intentionally does NOT auto-fire because
// `preferredCard?.card === null` can be transiently true (e.g. after `resetPreferredPaymentMethodErrors`
// mutates the card field, or before `setCardsForExpiryLimit` runs with a populated `started` payload),
// which previously enrolled users with saved cards and diluted the variant-A pool.
//
// Dispatches synchronously rather than using the `useExperimentTrigger` setState+useEffect
// indirection: callers invoke this immediately before `router.replace`, so the LoggedIn page
// can unmount in the next render and skip a deferred effect. Calling `useTriggerExperimentEvent`
// directly guarantees the Redux dispatch + analytics logEvent happen in the same execution as
// the trigger() invocation.
export const useAddPaymentMethodFlowExperimentTrigger = (): (() => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return () => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment)
    }
  }
}
